import { Card, CardContent, Grid, Paper, Typography, IconButton, Tooltip, Button } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useEffect, useState, useRef } from 'react';
import { parentUrl } from '../../../../utils/constants';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../reducers';
import { useAppDispatch } from '../../../../store/store';
import WidgetLoading from '../../Shared/WidgetLoading';
import { isDefaultDate } from '../../../../utils/common';
import { TransitionalCareStepTypeEnum } from '../../../../Enum/TransitionalCareStepTypeEnum';
import { toDate, formatDate } from '../../../../utils/dateHelpers';
import { FacilityTypeEnum } from '../../../../Enum/FacilityTypeEnum';
import Authorization from '../../../../utils/Authorization';
import b64toBlob from 'b64-to-blob';
import TeamDropdown from '../../../Shared/TeamDropdown';
import { fetchTeams } from '../../../../store/careTeams.slice';
import { fetchTransitionalCareWidget, exportTransitionalCareWidget } from '../../../Widgets/WidgetService';

const TransitionalCare = (props) => {
    const dispatch = useAppDispatch();
    const authData = new Authorization();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const [transitionalCare, setTransitionalCare] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [show, setShow] = useState(false);
    const ref = useRef(null);
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [showInActiveTeams, setShowInActiveTeams] = useState(false);
    const [selectedTeam, setSelectedTeam] = useState(authData.getDefaultTeamId('All'));

    useEffect(() => {
        dispatch(fetchTeams(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleClick = (event) => {
        setShow(!show);
    };

    const openDocumentHandler = (memberId: string) => {
        window.open(`${parentUrl}provider/members-list/member-chart/${memberId}/transitional-care`, '_blank');
    };

    useEffect(() => {
        const changeFilter = async () => {
            try {
                setIsLoading(true);
                const response = await fetchTransitionalCareWidget(selectedTeam, showInActiveTeams);
                setTransitionalCare(response);
                setIsLoading(false);
            } catch (error) {
                setTransitionalCare([]);
                setIsLoading(false);
                console.log(error);
            }
        };
        changeFilter();
    }, [selectedTeam, showInActiveTeams]);

    const exportTransitionalCareWidgets = async () => {
        setDownLoading(true);
        const response = await exportTransitionalCareWidget(selectedTeam, showInActiveTeams);
        let blob = b64toBlob(response.base64String, 'text/csv');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `TransitionalCareWidget - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
        setDownLoading(false);
    };

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display={'inline-block'} variant="h6" marginRight={1} color={'primary'}>
                            Transitional Care
                        </Typography>
                        <Typography display={'inline-block'} variant="body1" color={'primary'} ref={ref}>
                            &#40;{transitionalCare.length}&#41;
                            <Tooltip
                                open={show}
                                title={
                                    <div>
                                        <Typography variant="caption">Members in transitional care</Typography>
                                    </div>
                                }
                                PopperProps={{ style: { zIndex: 1000 } }}
                            >
                                <IconButton onClick={handleClick}>
                                    <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                </IconButton>
                            </Tooltip>
                        </Typography>
                    </Grid>
                    {props.hasElevatedFilterAccess && (
                        <Grid size={4} marginTop={2}>
                            <TeamDropdown
                                teams={teams}
                                selectedTeam={selectedTeam}
                                onChange={(id) => setSelectedTeam(id)}
                                showInactive={showInActiveTeams}
                                onToggleInactive={setShowInActiveTeams}
                            />
                        </Grid>
                    )}
                    <Grid size={6}></Grid>
                    <Grid size={2} display={'flex'} maxHeight="55px" marginTop="12px">
                        <Button
                            style={{ minWidth: '120px', marginLeft: 'auto' }}
                            onClick={() => exportTransitionalCareWidgets()}
                            variant="contained"
                            color="success"
                            disabled={downLoading || transitionalCare?.length === 0}
                        >
                            {downLoading ? 'Exporting..' : 'Export'}
                        </Button>
                    </Grid>
                </Grid>
                <Grid
                    container
                    spacing={2}
                    marginTop={2}
                    paddingLeft={2}
                    height={props.hasElevatedFilterAccess ? '62vh' : '65vh'}
                    sx={{ overflowY: 'scroll', position: 'relative' }}
                >
                    {isLoading ? (
                        <WidgetLoading />
                    ) : transitionalCare.length > 0 ? (
                        transitionalCare.map((member) => {
                            const admissionStep = member.TransitionSteps.find((step) => !step.IsDeleted && step.TransitionStepType === TransitionalCareStepTypeEnum.Admission);
                            const dischargeSteps = member.TransitionSteps.filter((step) => !step.IsDeleted && step.TransitionStepType === TransitionalCareStepTypeEnum.Discharge);
                            const lastStep = member.TransitionSteps[member.TransitionSteps.length - 1];
                            const address = [
                                lastStep?.Facility?.Name,
                                lastStep?.Facility?.Address,
                                lastStep?.Facility?.City,
                                lastStep?.Facility?.State,
                                lastStep?.Facility?.ZipCode,
                            ];
                            const movedTo = lastStep?.TransitionStepType === TransitionalCareStepTypeEnum.Discharge ? lastStep?.Address : address.join(', ');
                            const getFacilityTypeShortName = (admissionType?: FacilityTypeEnum) => {
                                switch (admissionType) {
                                    case FacilityTypeEnum.PhysicalHealth:
                                        return 'PH';

                                    case FacilityTypeEnum.BehavioralHealthLevel1:
                                    case FacilityTypeEnum.BehavioralHealthLevel2:
                                        return 'BH';

                                    default:
                                        return '-';
                                }
                            };
                            return (
                                <Grid key={member.Id} size={{ xs: 6 }}>
                                    <Card
                                        sx={{
                                            ':hover': {
                                                cursor: 'pointer',
                                                boxShadow: 10,
                                                border: 0,
                                            },
                                            boxShadow: 0,
                                        }}
                                        onClick={() => openDocumentHandler(member.MemberId)}
                                        variant="outlined"
                                    >
                                        <CardContent
                                            sx={{ display: 'flex', flexDirection: 'column', alignItem: 'flex-start', justifyContent: 'space-between' }}
                                        >
                                            <Grid container spacing={2}>
                                                <Grid size={6}>
                                                    <Typography color="text.secondary" variant="body2">
                                                        Member
                                                    </Typography>
                                                    <Typography fontWeight={'bold'} variant="body2" color={member.Priority === 3 ? 'error.main' : ''}>
                                                        {member.MemberName}
                                                    </Typography>
                                                </Grid>

                                                <Grid size={6}>
                                                    <Typography color="text.secondary" variant="body2">
                                                        Health Plan
                                                    </Typography>
                                                    <Typography fontWeight={'bold'} variant="body2" color={member.Priority === 3 ? 'error.main' : ''}>
                                                        {member.HealthPlan}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={6}>
                                                    <Typography color="text.secondary" variant="body2">
                                                        Admission Date
                                                    </Typography>
                                                    <Typography fontWeight={'bold'} variant="body2" color={member.Priority === 3 ? 'error.main' : ''}>
                                                        {isDefaultDate(admissionStep?.AdmissionDate)
                                                            ? '-'
                                                            : (() => {
                                                                const d = toDate(admissionStep?.AdmissionDate);
                                                                return d ? formatDate(d, 'MM/dd/yyyy') : '-';
                                                            })()}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={6}>
                                                    <Typography color="text.secondary" variant="body2">
                                                        Stage
                                                    </Typography>
                                                    <Typography fontWeight={'bold'} variant="body2" color={member.Priority === 3 ? 'error.main' : ''}>
                                                        {TransitionalCareStepTypeEnum[lastStep.TransitionStepType]}
                                                    </Typography>
                                                </Grid>

                                                <Grid size={6}>
                                                    <Typography color="text.secondary" variant="body2">
                                                        Admission Type
                                                    </Typography>
                                                    <Typography fontWeight={'bold'} variant="body2" color={member.Priority === 3 ? 'error.main' : ''}>
                                                        {getFacilityTypeShortName(admissionStep?.AdmissionType)}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={6}>
                                                    <Typography color="text.secondary" variant="body2">
                                                        Discharge Date
                                                    </Typography>
                                                    <Typography fontWeight={'bold'} variant="body2" color={member.Priority === 3 ? 'error.main' : ''}>
                                                        {dischargeSteps.length > 0
                                                            ? (() => {
                                                                const d = toDate(dischargeSteps[dischargeSteps.length - 1].AdmissionDate);
                                                                return d ? formatDate(d, 'MM/dd/yyyy') : '-';
                                                            })()
                                                            : '-'}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={12}>
                                                    <Typography color="text.secondary" variant="body2">
                                                        Moved To
                                                    </Typography>
                                                    <Typography fontWeight={'bold'} variant="body2" color={member.Priority === 3 ? 'error.main' : ''}>
                                                        {movedTo.length > 0 ? movedTo : '-'}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            );
                        })
                    ) : (
                        <Grid size={12} textAlign="center">
                            <Typography variant="h6">No Matching Records</Typography>
                        </Grid>
                    )}
                </Grid>
            </Paper>
        </Grid>
    );
};

export default TransitionalCare;
